import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Plus, Pencil, Trash2, Puzzle, Loader2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import useMoedaAtiva, {
	simboloMoeda,
	formatarMoeda,
	moedaPlataforma,
} from "../../hooks/useMoedaAtiva";

// A plataforma nem sempre cobra em real: pelo Stripe a moeda é a que o
// operador escolheu. Ver "R$" numa cobrança em euro seria mentira.
const brl = (v) => formatarMoeda(v, moedaPlataforma());

const Modules = () => {
	// Puxa a moeda da plataforma; os formatadores acima leem dela.
	useMoedaAtiva();

	const [modules, setModules] = useState([]);
	const [disponiveis, setDisponiveis] = useState([]);
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);

	const [modalOpen, setModalOpen] = useState(false);
	const [form, setForm] = useState({});
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selecionado, setSelecionado] = useState(null);

	const carregar = useCallback(async () => {
		try {
			const [lista, chaves] = await Promise.all([
				api.get("/modules"),
				api.get("/modules/available"),
			]);
			setModules(lista.data);
			setDisponiveis(chaves.data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const salvar = async () => {
		setSalvando(true);
		try {
			if (form.id) {
				await api.put(`/modules/${form.id}`, {
					name: form.name,
					description: form.description,
					price: Number(form.price) || 0,
					isActive: form.isActive,
				});
			} else {
				await api.post("/modules", {
					key: form.key,
					name: form.name,
					description: form.description,
					price: Number(form.price) || 0,
				});
			}
			toast.success("Módulo salvo.");
			setModalOpen(false);
			setForm({});
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluir = async () => {
		try {
			await api.delete(`/modules/${selecionado.id}`);
			toast.success("Módulo excluído.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionado(null);
	};

	// Chaves ainda não cadastradas: só elas podem virar módulo novo.
	const naoCadastradas = disponiveis.filter((d) => !d.alreadyRegistered);

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionado && `Excluir ${selecionado.name}?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionado(null);
				}}
				onConfirm={excluir}
			>
				Módulos já vendidos a alguma empresa não podem ser excluídos.
			</ConfirmationModal>

			<div className="flex flex-wrap items-center gap-2 border-b px-4 py-3 sm:px-6">
				<div className="min-w-0 flex-1">
					<h1 className="truncate text-lg font-semibold tracking-tight">
						Módulos
					</h1>
					<p className="text-xs text-muted-foreground">
						Recursos que você vende separadamente do plano
					</p>
				</div>
				<Button
					className="h-9"
					disabled={naoCadastradas.length === 0}
					onClick={() => {
						setForm({ key: "", name: "", price: 0, isActive: true });
						setModalOpen(true);
					}}
				>
					<Plus className="h-4 w-4" />
					<span className="hidden sm:inline">Novo módulo</span>
					<span className="sm:hidden">Novo</span>
				</Button>
			</div>

			<div className="flex-1 overflow-auto p-4 sm:p-6">
				{carregando ? (
					<div className="flex justify-center py-16">
						<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
					</div>
				) : modules.length === 0 ? (
					<div className="rounded-xl border bg-card py-16 text-center">
						<Puzzle className="mx-auto h-8 w-8 text-muted-foreground/50" />
						<p className="mt-3 text-sm font-medium">Nenhum módulo cadastrado</p>
						<p className="mt-1 text-xs text-muted-foreground">
							Módulos permitem vender um recurso sem trocar o plano do cliente.
						</p>
					</div>
				) : (
					<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
						{modules.map((modulo) => (
							<div
								key={modulo.id}
								className={cn(
									"flex flex-col rounded-xl border bg-card p-4",
									!modulo.isActive && "opacity-60"
								)}
							>
								<div className="flex items-start justify-between gap-2">
									<div className="min-w-0">
										<p className="truncate font-medium">{modulo.name}</p>
										<code className="text-[10px] text-muted-foreground">
											{modulo.key}
										</code>
									</div>
									{!modulo.isActive && (
										<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
											Inativo
										</span>
									)}
								</div>

								<p className="mt-2 flex-1 text-xs leading-relaxed text-muted-foreground">
									{modulo.description || "Sem descrição."}
								</p>

								<div className="mt-3 flex items-center justify-between border-t pt-3">
									<span className="text-lg font-semibold tabular-nums">
										{Number(modulo.price) > 0 ? brl(modulo.price) : "Grátis"}
									</span>
									<div className="flex gap-1">
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8"
											aria-label={`Editar ${modulo.name}`}
											onClick={() => {
												setForm({
													id: modulo.id,
													key: modulo.key,
													name: modulo.name,
													description: modulo.description || "",
													price: modulo.price,
													isActive: modulo.isActive,
												});
												setModalOpen(true);
											}}
										>
											<Pencil className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8"
											aria-label={`Excluir ${modulo.name}`}
											onClick={() => {
												setSelecionado(modulo);
												setConfirmOpen(true);
											}}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			<Dialog open={modalOpen} onOpenChange={setModalOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{form.id ? "Editar módulo" : "Novo módulo"}
						</DialogTitle>
						<DialogDescription>
							O módulo controla o acesso a uma área do sistema.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						{!form.id && (
							<div className="space-y-1.5">
								<Label>Área do sistema</Label>
								<Select
									value={form.key}
									onValueChange={(v) => {
										const escolhida = naoCadastradas.find((d) => d.key === v);
										setForm({
											...form,
											key: v,
											name: escolhida?.name || "",
											description: escolhida?.description || "",
										});
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder="Selecione" />
									</SelectTrigger>
									<SelectContent>
										{naoCadastradas.map((d) => (
											<SelectItem key={d.key} value={d.key}>
												{d.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									Quem não contratar este módulo não acessa essa área.
								</p>
							</div>
						)}

						<div className="space-y-1.5">
							<Label htmlFor="nomeModulo">Nome</Label>
							<Input
								id="nomeModulo"
								value={form.name || ""}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
							/>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="descModulo">Descrição</Label>
							<Textarea
								id="descModulo"
								rows={2}
								value={form.description || ""}
								onChange={(e) =>
									setForm({ ...form, description: e.target.value })
								}
							/>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="precoModulo">
								Preço mensal ({simboloMoeda(moedaPlataforma())})
							</Label>
							<Input
								id="precoModulo"
								type="number"
								step="0.01"
								min="0"
								value={form.price ?? 0}
								onChange={(e) => setForm({ ...form, price: e.target.value })}
							/>
							<p className="text-xs text-muted-foreground">
								Use 0 para incluir sem cobrar.
							</p>
						</div>

						{form.id && (
							<div className="flex items-center justify-between rounded-lg border p-3">
								<div>
									<Label htmlFor="ativoModulo" className="text-sm font-medium">
										Disponível para venda
									</Label>
									<p className="mt-0.5 text-xs text-muted-foreground">
										Desative para parar de oferecer sem afetar quem já tem.
									</p>
								</div>
								<Switch
									id="ativoModulo"
									checked={form.isActive}
									onCheckedChange={(v) => setForm({ ...form, isActive: v })}
								/>
							</div>
						)}
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModalOpen(false)}>
							Cancelar
						</Button>
						<Button
							disabled={salvando || !form.key || !form.name}
							onClick={salvar}
						>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Salvar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</MainContainer>
	);
};

export default Modules;
